// Which visualisations suit a report's shape. Shared by the builder's chart picker and the
// renderer, so a chosen chart and what is drawn never disagree without an explanation.

import type { ChartKind } from './charts';
import type { QueryResult, QueryResultColumn } from './types';
import { resolveFormat } from './format';

export interface ChartShape {
    reportType: string | null | undefined;
    dimensions: number;
    measures: number;
    /** Distinct horizontal-axis values in the current preview, when available. */
    categories?: number;
    /** Distinct currencies in the result when money totals are grouped by currency (more than one never share an axis). */
    currencies?: number;
    /** The measures do not share one unit and format (a count beside a money total). */
    mixedUnits?: boolean;
    /** Each measure's aggregate, in order, when known (from the spec or the result). */
    aggregates?: Array<string | null | undefined>;
    /** Some measure value in the current result is below zero. */
    negatives?: boolean;
    /** The report has a target (`display.target`), so a gauge can show progress. */
    target?: boolean;
}

export interface ChartChoice {
    kind: ChartKind;
    label: string;
    icon: string;
    /** What the chart is for, shown whatever the report: never replaced by the reason it is unavailable. */
    description: string;
    available: boolean;
    recommended: boolean;
    /** Why the chart cannot show this report, phrased as what it needs. */
    reason?: string;
}

const CATALOGUE: Array<{ kind: ChartKind; label: string; icon: string; description: string }> = [
    { kind: 'line', label: 'Line', icon: 'bi-graph-up', description: 'Trends over time.' },
    { kind: 'area', label: 'Area', icon: 'bi-graph-up-arrow', description: 'Trends with a filled area.' },
    { kind: 'bar', label: 'Bar', icon: 'bi-bar-chart-steps', description: 'Compare categories (horizontal).' },
    { kind: 'column', label: 'Column', icon: 'bi-bar-chart-line', description: 'Compare categories (vertical).' },
    { kind: 'stackedColumn', label: 'Stacked columns', icon: 'bi-bar-chart', description: 'Totals and their parts.' },
    { kind: 'combo', label: 'Combo', icon: 'bi-bar-chart-line-fill', description: 'Columns and lines together.' },
    { kind: 'scatter', label: 'Scatter', icon: 'bi-diagram-3', description: 'Relate two calculations.' },
    { kind: 'waterfall', label: 'Waterfall', icon: 'bi-bar-chart-steps', description: 'A running total, step by step.' },
    { kind: 'donut', label: 'Donut', icon: 'bi-circle', description: 'Shares of a whole.' },
    { kind: 'pie', label: 'Pie', icon: 'bi-pie-chart', description: 'Shares of a whole.' },
    { kind: 'treemap', label: 'Treemap', icon: 'bi-grid-1x2', description: 'Shares of a whole, as tiles.' },
    { kind: 'kpi', label: 'Single number', icon: 'bi-123', description: 'One overall value.' },
    { kind: 'gauge', label: 'Gauge', icon: 'bi-speedometer2', description: 'One value against a target.' },
    { kind: 'pivot', label: 'Pivot table', icon: 'bi-grid-3x3', description: 'Groups as rows and columns.' },
    { kind: 'heatmap', label: 'Heatmap', icon: 'bi-grid-3x3-gap-fill', description: 'Two groupings, coloured by value.' },
    { kind: 'table', label: 'Table', icon: 'bi-table', description: 'Grouped values in rows.' },
];

/** The short, stable description of a chart type; a table of records and a stage breakdown describe themselves. */
export function chartDescription(kind: ChartKind, reportType?: string | null): string {
    if (kind === 'table' && reportType === 'recordList') return 'Records with the columns you choose.';
    if (kind === 'funnel') return 'Records at each stage.';
    return CATALOGUE.find(c => c.kind === kind)?.description ?? CATALOGUE.find(c => c.kind === 'table')!.description;
}

export function chartLabel(kind: ChartKind): string {
    return CATALOGUE.find(c => c.kind === kind)?.label ?? (kind === 'funnel' ? 'Stage breakdown' : 'Table');
}

/** The format identity of a measure: measures with the same signature can share an axis or a stack. */
export function unitSignature(column: QueryResultColumn): string {
    const f = resolveFormat(column, null);
    return `${f.numberFormat}|${f.numberFormat === 'currency' ? column.currencyAlias ?? f.currency ?? '' : ''}`;
}

/** The dimension that splits money totals by currency, when the engine added one. */
export function currencyDimension(result: Pick<QueryResult, 'columns'>): QueryResultColumn | null {
    const aliases = new Set(result.columns.filter(c => c.kind === 'measure' && c.currencyAlias).map(c => c.currencyAlias!));
    return result.columns.find(c => c.kind === 'dimension' && aliases.has(c.alias)) ?? null;
}

/**
 * Row keys holding each group's currency for money stored as text with its own code (`__analytics_currency_<i>`).
 * They are in every row but never in `columns`, so they are read for formatting and never drawn as a column.
 */
export function hiddenCurrencyAliases(result: Pick<QueryResult, 'columns'>): string[] {
    const columns = new Set(result.columns.map(c => c.alias));
    return [...new Set(result.columns.filter(c => c.kind === 'measure' && c.currencyAlias && !columns.has(c.currencyAlias)).map(c => c.currencyAlias!))];
}

/** The currency (or currencies, for several text-money measures) a row's money is in, by dimension or hidden key. */
export function rowCurrencyKey(result: Pick<QueryResult, 'columns'>): ((row: Record<string, unknown>) => unknown) | null {
    const dimension = currencyDimension(result);
    if (dimension) return row => row[dimension.alias];
    const hidden = hiddenCurrencyAliases(result);
    if (!hidden.length) return null;
    return hidden.length === 1 ? row => row[hidden[0]] : row => hidden.every(a => row[a] == null) ? null : hidden.map(a => row[a] ?? '–').join(' / ');
}

/** Shape facts only a result can tell: currencies present, whether measures mix units, and negative values. */
export function resultShapeTraits(result: Pick<QueryResult, 'columns' | 'rows'>): Pick<ChartShape, 'currencies' | 'mixedUnits' | 'negatives'> {
    const measures = result.columns.filter(c => c.kind === 'measure');
    const currency = rowCurrencyKey(result);
    const traits: Pick<ChartShape, 'currencies' | 'mixedUnits' | 'negatives'> = {};
    if (currency) traits.currencies = new Set(result.rows.map(currency).filter(v => v != null && v !== '')).size;
    if (measures.length > 1 && new Set(measures.map(unitSignature)).size > 1) traits.mixedUnits = true;
    if (result.rows.some(row => measures.some(m => { const v = row[m.alias]; return v != null && v !== '' && Number(v) < 0; }))) traits.negatives = true;
    return traits;
}

/**
 * The aggregate of each measure in a result, for charts that need values that add up. A dataset measure the
 * server marks as not re-aggregable counts as an average whatever its aggregate.
 */
export function resultAggregates(result: Pick<QueryResult, 'columns'>): Array<string | null | undefined> {
    return result.columns.filter(c => c.kind === 'measure').map(c => c.reaggregable === false && c.aggregate !== 'percentOfTotal' ? 'avg' : c.aggregate);
}

/** Line for trends, bars for comparing categories, a single number for one total, a table for records. */
export function recommendedChart(shape: ChartShape): ChartKind {
    if (shape.reportType === 'pivot' && shape.dimensions >= 2 && shape.measures >= 1) return 'pivot';
    if (shape.dimensions > 2) return 'table';
    if (shape.reportType === 'timeSeries' && shape.categories !== undefined && shape.categories < 2) return 'table';
    switch (shape.reportType) {
        case 'recordList': return 'table';
        case 'funnel': return 'funnel';
        case 'pivot': return 'table';
        case 'timeSeries': return shape.dimensions >= 1 && shape.measures >= 1 ? 'line' : 'table';
    }
    // A target turns one total into progress towards it.
    if (shape.dimensions === 0 && shape.measures === 1) return shape.target && !(shape.currencies && shape.currencies > 1) ? 'gauge' : 'kpi';
    // Horizontal bars have one value axis; measures in different units need a column chart's second axis.
    if (shape.dimensions === 1 && shape.measures >= 1) return shape.mixedUnits ? 'column' : 'bar';
    if (shape.dimensions === 2 && shape.measures >= 1)
        return shape.measures === 1 && !(shape.currencies && shape.currencies > 1) ? 'stackedColumn' : 'column';
    return 'table';
}

/** Null when the chart can show this shape, otherwise what it needs. */
export function unsupportedReason(kind: ChartKind, shape: ChartShape): string | null {
    const { dimensions: d, measures: m } = shape;
    if (shape.reportType === 'recordList') return kind === 'table' ? null : 'Record lists show as a table.';
    if (shape.reportType === 'funnel') return kind === 'funnel' || kind === 'table' ? null : 'Stage reports show as stages or a table.';
    switch (kind) {
        case 'table': return null;
        case 'kpi': return d === 0 && m === 1 ? null : 'Needs one calculation and no grouping.';
        case 'bar': case 'column': case 'line': case 'area':
            if (d > 2) return 'Needs one Group by and at most one Split by.';
            if (!(d >= 1 && m >= 1)) return 'Needs a Group by and a calculation.';
            if (kind === 'bar' && shape.mixedUnits) return 'Needs calculations in one unit. Try Column.';
            return null;
        case 'stackedColumn':
            if (!((d === 2 && m === 1) || (d === 1 && m > 1))) return 'Needs a Group by and Split by, or 2+ calculations.';
            if (shape.mixedUnits) return 'Needs calculations in one unit. Try Column.';
            if (shape.currencies && shape.currencies > 1) return 'Can\'t add different currencies. Try Column.';
            return null;
        case 'donut': case 'pie':
            return d === 1 && m === 1 ? null : 'Needs one Group by and one calculation.';
        case 'pivot': return d >= 2 && m >= 1 ? null : 'Needs 2+ groupings and a calculation.';
        case 'heatmap': return d === 2 && m === 1 ? null : 'Needs two groupings and one calculation.';
        case 'combo':
            return d === 1 && m >= 2 ? null : 'Needs one Group by and 2+ calculations.';
        case 'scatter':
            return d === 1 && (m === 2 || m === 3) ? null
                : 'Needs one Group by and 2–3 calculations.';
        case 'gauge':
            if (!(d === 0 && m === 1)) return 'Needs one calculation, no grouping and a target.';
            if (shape.currencies && shape.currencies > 1) return 'Can\'t target mixed currencies. Try Single number.';
            return null;
        case 'waterfall': {
            if (!(d === 1 && m === 1)) return 'Needs one Group by and one calculation.';
            const agg = shape.aggregates?.[0];
            if (agg && agg !== 'sum' && agg !== 'count') return 'Needs a count or total (sum).';
            return null;
        }
        case 'treemap': {
            if (!((d === 1 || d === 2) && m === 1)) return 'Needs 1–2 groupings and one calculation.';
            const agg = shape.aggregates?.[0];
            if (agg && agg !== 'sum' && agg !== 'count' && agg !== 'percentOfTotal') return 'Needs a count, total or percentage of total.';
            if (shape.negatives) return 'Can\'t draw negative values. Try Bar.';
            return null;
        }
        default: return null;
    }
}

/** Chart roles are explicit; tables retain any number of grouping fields. */
export function groupingLimit(shape: ChartShape, selected?: string | null): number {
    if (shape.reportType === 'recordList') return Infinity;
    if (!selected) return shape.reportType === 'pivot' || shape.dimensions > 2 ? Infinity : 2;
    const kind = selected;
    if (kind === 'table' || kind === 'pivot') return Infinity;
    if (kind === 'kpi' || kind === 'gauge') return 0;
    if (kind === 'pie' || kind === 'donut' || kind === 'combo' || kind === 'scatter' || kind === 'waterfall') return 1;
    return 2;
}

export function chartChoices(shape: ChartShape): ChartChoice[] {
    const recommended = recommendedChart(shape);
    return CATALOGUE.map(entry => {
        const reason = unsupportedReason(entry.kind, shape);
        return { ...entry, available: reason === null, recommended: entry.kind === recommended, reason: reason ?? undefined };
    });
}

/** Explain the rule rather than presenting the suggestion as a judgment of the data. */
export function recommendationReason(shape: ChartShape): string {
    if (recommendedChart(shape) === 'pivot') return 'Recommended: Pivot table, for row and column groups.';
    if (shape.dimensions > 2) return 'Recommended: Summary table, one column per grouping.';
    if (shape.reportType === 'timeSeries') {
        if (shape.categories === 0) return 'Recommended: Summary table, until there are results.';
        if (shape.categories === 1) return 'Recommended: Summary table, as there is only one time point.';
        if (recommendedChart(shape) === 'table') return 'Recommended: Summary table, until there is a grouping and calculation.';
        return 'Recommended: Line, for values over time.';
    }
    switch (recommendedChart(shape)) {
        case 'kpi': return 'Recommended: Single number, for one calculation and no grouping.';
        case 'gauge': return 'Recommended: Gauge, for one calculation with a target.';
        case 'bar': return 'Recommended: Bar, for comparing categories.';
        case 'stackedColumn': return 'Recommended: Stacked columns, for a group and split.';
        case 'column': return shape.currencies && shape.currencies > 1 ? 'Recommended: Column, so currencies stay separate.'
            : shape.mixedUnits ? 'Recommended: Column, so each unit gets its own axis.'
            : 'Recommended: Column, for several calculations.';
        case 'pivot': return 'Recommended: Pivot table, for row and column groups.';
        case 'table': return shape.reportType === 'recordList' ? 'Shows records with the columns you choose.' : 'Recommended: Summary table.';
        default: return 'Suggested from the report type and grouping.';
    }
}
